import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

export function formatDate(dateString, options = {}) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(date);
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateString);
}

export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function getStatusColor(status) {
  const statusColors = {
    // General
    active: 'badge-success',
    inactive: 'badge-default',
    archived: 'badge-default',
    
    // Leads
    new: 'badge-info',
    contacted: 'badge-warning',
    qualified: 'badge-success',
    converted: 'badge-success',
    lost: 'badge-error',
    
    // Cases
    open: 'badge-info',
    in_progress: 'badge-warning',
    pending: 'badge-warning',
    closed: 'badge-default',
    
    // Tasks
    completed: 'badge-success',
    cancelled: 'badge-error',
    
    // Invoices
    draft: 'badge-default',
    sent: 'badge-info',
    paid: 'badge-success',
    overdue: 'badge-error',
  };
  
  return statusColors[status] || 'badge-default';
}

export function getPriorityColor(priority) {
  const priorityColors = {
    low: 'text-slate-500',
    medium: 'text-yellow-600',
    high: 'text-orange-600',
    urgent: 'text-red-600',
  };
  
  return priorityColors[priority] || 'text-slate-500';
}

export function truncateText(text, maxLength = 100) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function generateId(prefix = 'id') {
  return `${prefix}_${Math.random().toString(36).substr(2, 9)}`;
}

export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

// Feature access check helper
export function hasFeatureAccess(userTier, feature) {
  const tierFeatures = {
    basic: ['clients', 'cases', 'documents', 'tasks'],
    intermediate: ['clients', 'cases', 'documents', 'tasks', 'calendar', 'collaboration', 'messaging'],
    advanced: ['clients', 'cases', 'documents', 'tasks', 'calendar', 'collaboration', 'messaging', 'ai', 'financials', 'trust_accounting', 'reports', 'time_billing'],
  };
  
  const features = tierFeatures[userTier] || tierFeatures.basic;
  return features.includes(feature);
}

// Practice areas for legal cases
export const PRACTICE_AREAS = [
  'Corporate Law',
  'Criminal Law',
  'Family Law',
  'Real Estate',
  'Personal Injury',
  'Immigration',
  'Intellectual Property',
  'Employment Law',
  'Tax Law',
  'Estate Planning',
  'Bankruptcy',
  'Civil Litigation',
  'Environmental Law',
  'Healthcare Law',
  'Other',
];

// Document types
export const DOCUMENT_TYPES = [
  { value: 'contract', label: 'Contract' },
  { value: 'pleading', label: 'Pleading' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'template', label: 'Template' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'court_filing', label: 'Court Filing' },
  { value: 'other', label: 'Other' },
];

// Case statuses
export const CASE_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending', label: 'Pending' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

// Lead pipeline stages
export const PIPELINE_STAGES = [
  { value: 'inquiry', label: 'Inquiry', color: 'bg-blue-100' },
  { value: 'consultation', label: 'Consultation', color: 'bg-yellow-100' },
  { value: 'evaluation', label: 'Evaluation', color: 'bg-purple-100' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-orange-100' },
  { value: 'closed', label: 'Closed', color: 'bg-green-100' },
];

/**
 * Download a file from blob data
 * @param {Blob|ArrayBuffer|any} data - The file data
 * @param {string} filename - The filename for download
 * @param {string} mimeType - The MIME type (default: application/pdf)
 */
export function downloadFile(data, filename, mimeType = 'application/pdf') {
  try {
    // Convert to blob if needed
    let blob;
    if (data instanceof Blob) {
      blob = data;
    } else if (data instanceof ArrayBuffer) {
      blob = new Blob([data], { type: mimeType });
    } else if (typeof data === 'string') {
      // Handle base64 encoded data
      try {
        const byteCharacters = atob(data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: mimeType });
      } catch {
        blob = new Blob([data], { type: mimeType });
      }
    } else {
      blob = new Blob([data], { type: mimeType });
    }
    
    // Check if blob has content
    if (!blob || blob.size === 0) {
      console.error('Download failed: file is empty');
      return false;
    }
    
    console.log('Downloading file:', filename, 'Size:', blob.size, 'Type:', blob.type || mimeType);
    
    // Create object URL
    const url = window.URL.createObjectURL(blob);
    
    // Try multiple download methods for better browser compatibility
    
    // Method 1: Use navigator.msSaveOrOpenBlob for IE/Edge Legacy
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return true;
    }
    
    // Method 2: Create anchor and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Important: Do NOT set target="_blank" for downloads
    // That can cause issues with some browsers
    
    // Add to DOM
    document.body.appendChild(link);
    
    // Trigger download immediately
    link.click();
    
    // Cleanup after a short delay
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    }, 500);
    
    return true;
  } catch (error) {
    console.error('Download error:', error);
    return false;
  }
}

/**
 * Open a file in a new browser tab (alternative to download)
 * @param {Blob|ArrayBuffer|any} data - The file data
 * @param {string} mimeType - The MIME type (default: application/pdf)
 */
export function openFileInNewTab(data, mimeType = 'application/pdf') {
  let blob;
  if (data instanceof Blob) {
    blob = data;
  } else {
    blob = new Blob([data], { type: mimeType });
  }
  
  if (blob.size === 0) {
    console.error('Cannot open: file is empty');
    return false;
  }
  
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  
  // Revoke after a delay
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 60000); // Keep URL valid for 1 minute
  
  return true;
}
